import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiCreditCard, FiRefreshCw } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import type { ActiveSubscriptionItem } from '../types';

interface ActiveSubscriptionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const paymentMethodLabel: Record<string, string> = {
  card: 'Cartão',
  pix: 'PIX',
  boleto: 'Boleto',
  unknown: 'Desconhecido',
};

export const ActiveSubscriptionsDrawer: React.FC<ActiveSubscriptionsDrawerProps> = ({ isOpen, onClose }) => {
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-dashboard-active-subscriptions'],
    queryFn: () => apiGet<ActiveSubscriptionItem[]>('/dashboard/active-subscriptions'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiCreditCard className="w-5 h-5" />}
      title="Assinaturas Ativas"
      subtitle={items ? `${items.length} assinatura${items.length !== 1 ? 's' : ''}` : undefined}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={88} variant="rectangular" />
          ))}
        </div>
      ) : items && items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.companyId}
              className="p-3 rounded-lg space-y-2"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              {/* Header: empresa + valor */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {item.companyName}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                    {item.planName}
                  </p>
                </div>
                <p className="text-sm font-semibold tabular-nums flex-shrink-0" style={{ color: 'var(--theme-primary)' }}>
                  {formatCurrency(item.monthlyValue)}<span className="text-xs font-normal" style={{ color: 'var(--theme-muted-text)' }}>/mês</span>
                </p>
              </div>

              {/* Meta: badges + datas */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={item.cycleDays === 365 ? 'info' : 'default'}>
                  {item.cycleDays === 365 ? 'Anual' : 'Mensal'}
                </Badge>
                <Badge variant="outline">{paymentMethodLabel[item.paymentMethod] || item.paymentMethod}</Badge>
                {item.autoRechargeEnabled && (
                  <Badge variant="success" className="inline-flex items-center gap-1">
                    <FiRefreshCw className="w-3 h-3" />
                    Auto-recarga
                  </Badge>
                )}
                <span style={{ color: 'var(--theme-muted-text)' }}>
                  Desde {formatDate(item.subscribedAt)} · Próx. {formatDate(item.nextRenewalAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiCreditCard className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhuma assinatura ativa
          </p>
        </div>
      )}
    </Drawer>
  );
};
